import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  Input,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {YouTubePlayer} from '@angular/youtube-player';
import {tuiClamp, TuiSwipe, TuiSwipeEvent} from '@taiga-ui/cdk';
import {TuiButton, TuiDialogContext, TuiIcon} from '@taiga-ui/core';
import {TuiPreview, TuiPreviewDialogService} from '@taiga-ui/kit';
import {Picture} from "../../interfaces/Picture";
import {NgxImageZoomModule} from 'ngx-image-zoom'

export interface ImagePreviewPicture {
  src: string;
  original?: string;
  videoCode?: string;  // YouTube video ID для <youtube-player>
  videoLink?: string;  // URL для iframe (Rutube, VK и др.)
  picture?: Picture;
}

@Component({
  selector: 'image-preview',
  templateUrl: './image-preview.template.html',
  styleUrl: './image-preview.style.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    TuiButton,
    TuiPreview,
    ReactiveFormsModule,
    TuiSwipe,
    NgClass,
    YouTubePlayer,
    NgxImageZoomModule,
    TuiIcon
]
})
export class ImagePreviewComponent {
  @Input() title: string;
  @Input() showMeta = false;
  @ViewChild('template') template: TemplateRef<TuiDialogContext>;
  pictures: ImagePreviewPicture[];
  index = 0;
  dialog: any;
  canExpand = false;
  canCollapse = false;
  cleanView = false;
  storeHistory: boolean;
  playerVars = {
    'autoplay': 1,
    'width': 800,
    'height': 600,
  };

  private readonly previewService = inject(TuiPreviewDialogService);
  private readonly router = inject(Router);
  private readonly renderer = inject(Renderer2);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly safeUrlCache = new Map<string, SafeResourceUrl>();

  safeVideoUrl(url: string): SafeResourceUrl {
    if (!this.safeUrlCache.has(url)) {
      this.safeUrlCache.set(url, this.sanitizer.bypassSecurityTrustResourceUrl(url));
    }
    return this.safeUrlCache.get(url);
  }

  image(picture: ImagePreviewPicture = null) {
    return picture?.src || this.pictures[this.index].src;
  }

  get currentImage() {
    return this.pictures[this.index];
  }

  isVideo(picture: ImagePreviewPicture = this.currentImage): boolean {
    return !!(picture?.videoCode || picture?.videoLink);
  }

  show(index: number, pictures: ImagePreviewPicture[], storeHistory = false): void {
    if (this.dialog) {
      return;
    }
    if (typeof window !== 'undefined') {
      this.playerVars.width = window.innerWidth * 0.7;
      this.playerVars.height = window.innerHeight * 0.7;
    }
    this.pictures = pictures;
    this.storeHistory = storeHistory;

    this.collapse(true);
    this.select(index);

    this.dialog = this.previewService.open(this.template).subscribe({
      complete: () => {
        this.onIndexChange(null);
        this.dialog = null;
      }
    });

    this.onIndexChange(this.index);
  }

  select(index: number) {
    this.index = index;
  }

  onIndexChange(index: number) {
    if (this.storeHistory) {
      this.router.navigate([], {queryParams: {img: index}, queryParamsHandling: 'merge', replaceUrl: true});
    }
  }

  click(event) {
    if (event.target?.tagName == 'TUI-PREVIEW') {
      this.close();
    }
  }

  onSwipe(swipe: TuiSwipeEvent): void {
    if (swipe.direction === 'left') {
      this.select(tuiClamp(this.index + 1, 0, this.pictures.length - 1));
    }

    if (swipe.direction === 'right') {
      this.select(tuiClamp(this.index - 1, 0, this.pictures.length - 1));
    }
  }

  open(): void {
    const image = new Image();
    image.src = this.image();
    window.open('').document.write(image.outerHTML);
  }

  close(): void {
    this.dialog?.complete();
    this.dialog = null;
  }

  setCleanView(value): void {
    this.cleanView = value;

    if (document.querySelector('tui-preview-dialog .thumb')) {
      this.renderer.setStyle(document.querySelector('tui-preview-dialog .thumb'), 'visibility', value ? 'hidden' : 'visible');
    }
  }

  download(): void {
    const downloadLink = document.createElement('a');
    downloadLink.href = this.image();
    downloadLink.download = this.title + ' ' + (this.index + 1);
    downloadLink.click();
  }

  expand() {
    // Фуллскринить нужно именно <tui-root>: его шаблон рендерит <tui-popups> (хост оверлеев,
    // где живёт превью) только пока document.fullscreenElement === tui-root (геттер parent →
    // сигнал top, обновляется на document:fullscreenchange). Если фуллскринить documentElement
    // или сам диалог, tui-root убирает <tui-popups> из DOM → превью уничтожается (выглядит как
    // закрытие). Фуллскрин самого tui-root оставляет popups на месте и показывает превью.
    document.querySelector('tui-root')?.requestFullscreen();
    this.canExpand = false;
    this.canCollapse = true;
  }

  collapse(onInit) {
    if (!onInit) {
      document.exitFullscreen();
    }
    this.canCollapse = false;
    this.canExpand = !!document.documentElement.requestFullscreen;
  }

  done() {
    const selector = 'tui-preview section .thumb';
    if (document.querySelector(selector)) {
      this.renderer.appendChild(document.querySelector('tui-preview-dialog'), document.querySelector(selector));
    }
  }

  @HostListener('document:keydown.ArrowUp', ['$event'])
  onUpHandler($event) {
    const btn: HTMLButtonElement = document.querySelector('button.t-sign_plus');
    if (btn) {
      $event.preventDefault();
      $event.stopPropagation();
      btn.click();
    }
  }

  @HostListener('document:keydown.ArrowDown', ['$event'])
  onDownHandler($event) {
    const btn: HTMLButtonElement = document.querySelector('button.t-sign_minus');
    if (btn) {
      $event.preventDefault();
      $event.stopPropagation();
      btn.click();
    }
  }
}
